import { inject, Injectable } from '@angular/core';
import type { FirestoreSshKey, SshKeyEntity } from '@jessie/commons';
import { firstValueFrom, Observable } from 'rxjs';
import { where } from '@angular/fire/firestore';
import { FIRESTORE_SERVICE } from './firestore.service';

@Injectable({ providedIn: 'root' })
export class SshKeyService {
  private firestoreService = inject(FIRESTORE_SERVICE);

  private keyPath(orgaId: string, orgaUserId: string): string {
    return `orga/${orgaId}/orgaUser/${orgaUserId}/sshKey`;
  }

  getKeys(orgaId: string, orgaUserId: string): Observable<FirestoreSshKey[]> {
    return this.firestoreService.getDocs<SshKeyEntity>(this.keyPath(orgaId, orgaUserId));
  }

  async addKey(orgaId: string, orgaUserId: string, publicKey: string, label: string): Promise<void> {
    const fingerprint = await computeFingerprint(publicKey);
    const path = this.keyPath(orgaId, orgaUserId);

    const existing = await firstValueFrom(
      this.firestoreService.getDocs<SshKeyEntity>(
        path,
        where('fingerprint', '==', fingerprint)
      )
    );
    if (existing.length > 0) {
      throw new Error('This SSH key has already been added.');
    }

    const entry: SshKeyEntity = {
      publicKey: publicKey.trim(),
      fingerprint,
      label: label.trim(),
      createdAt: new Date().toISOString(),
    };
    await this.firestoreService.createDoc<SshKeyEntity>(path, entry);
  }

  deleteKey(orgaId: string, orgaUserId: string, keyId: string): Promise<void> {
    return this.firestoreService.deleteDoc(`${this.keyPath(orgaId, orgaUserId)}/${keyId}`);
  }
}

/**
 * Computes the SSH fingerprint of a public key string using the Web Crypto API.
 * Parses the base64 key data from the OpenSSH format and hashes it with SHA-256.
 * Returns a fingerprint in the format `SHA256:base64...` — the same as `ssh-keygen -lf`.
 */
async function computeFingerprint(publicKey: string): Promise<string> {
  const parts = publicKey.trim().split(/\s+/);
  if (parts.length < 2) throw new Error('Invalid SSH public key format');

  const keyBase64 = parts[1];
  let keyBytes: Uint8Array;
  try {
    keyBytes = Uint8Array.from(atob(keyBase64), (c) => c.charCodeAt(0));
  } catch {
    throw new Error('Invalid SSH public key format: malformed base64 data');
  }
  const hashBuffer = await crypto.subtle.digest('SHA-256', keyBytes);
  const hashBase64 = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
  return `SHA256:${hashBase64}`;
}

/**
 * Validates that a string looks like a supported SSH public key.
 */
export function isValidSshPublicKey(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.startsWith('ssh-ed25519 ') ||
    trimmed.startsWith('ssh-rsa ') ||
    trimmed.startsWith('ecdsa-sha2-nistp256 ') ||
    trimmed.startsWith('ecdsa-sha2-nistp384 ') ||
    trimmed.startsWith('ecdsa-sha2-nistp521 ')
  );
}
